import { useMemo, VFC } from 'react';
import styled from 'styled-components';

import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import ExpressionEditor from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ExpressionEditor';
import ExpressionDisplay from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ExpressionEditor/Display';
import MaximizablePanel from 'dashboard/components-lib/MaximizablePanel';
import { generateJsonSchemaConfig, UsableObjectConfig } from 'dashboard/lib/workflows';
import { WorkflowError } from 'dashboard/models/workflow';
import { WorkflowActionCalculateV1 } from 'dashboard/models/workflow-version';
import Spacer from 'design-system/components/Spacer';
import useToggle from 'lib/hooks/useToggle';

const Clickable = styled.div`
  cursor: pointer;
`;

interface ParamsCalculateV1Props {
  editable: boolean;
  errors: WorkflowError[];
  params: WorkflowActionCalculateV1['params'];
  usableObjects: UsableObjectConfig[];
  updateActionParams: (newParams: WorkflowActionCalculateV1['params']) => void;
}

const ParamsCalculateV1: VFC<ParamsCalculateV1Props> = ({
  editable,
  errors,
  params,
  usableObjects,
  updateActionParams,
}) => {
  const variables = params.variables;
  const returnVariableId = params['return-variables'][0];
  const [showEditor, { toggle: toggleShowEditor, disable: hideEditor }] = useToggle(false);

  const varTokensToDisplayValue = useMemo(() => {
    return variables.reduce((hash: Record<string, string>, { id, name }) => {
      if (name) {
        hash[id] = name;
      }
      return hash;
    }, {});
  }, [variables]);

  const updateParams = <Key extends keyof WorkflowActionCalculateV1['params']>(
    key: Key,
    value: WorkflowActionCalculateV1['params'][Key],
  ) => {
    updateActionParams({
      ...params,
      [key]: value,
    });
  };

  const deleteVariable = (index: number) => {
    const newVariables = [...variables.slice(0, index), ...variables.slice(index + 1)];
    const newParams: WorkflowActionCalculateV1['params'] = {
      ...params,
      variables: newVariables,
    };
    if (variables[index].id === returnVariableId) {
      newParams['return-variables'] = [newVariables[0].id];
    }
    updateActionParams(newParams);
  };

  const { jsonSchema, tokensToDisplayValue } = useMemo(() => {
    return generateJsonSchemaConfig({
      usableObjects,
    });
  }, [usableObjects]);

  return (
    <>
      <Spacer height={2} />
      <ParamLabel labelText='Expression' />
      {editable ? (
        <Clickable id='params-calculate-v1__expression-display' onClick={toggleShowEditor}>
          <ExpressionDisplay
            hasError={!!errors.length}
            variables={variables}
            returnVariableId={returnVariableId}
            tokensToDisplayValue={{
              ...tokensToDisplayValue,
              ...varTokensToDisplayValue,
            }}
          />
        </Clickable>
      ) : (
        <ExpressionDisplay
          hasError={!!errors.length}
          variables={variables}
          returnVariableId={returnVariableId}
          tokensToDisplayValue={{
            ...tokensToDisplayValue,
            ...varTokensToDisplayValue,
          }}
        />
      )}
      {showEditor && (
        <MaximizablePanel
          title='Edit Expression'
          maximized
          outsideExceptionalIdPrefixes={['expression-editor__']}
          onOutsideClickAndEscPress={hideEditor}
        >
          <ExpressionEditor
            errors={errors}
            jsonSchema={jsonSchema}
            tokensToDisplayValue={tokensToDisplayValue}
            variables={variables}
            returnVariableId={returnVariableId}
            deleteVariable={deleteVariable}
            setVariables={(vars) => updateParams('variables', vars)}
            setReturnVariableId={(variableId) => updateParams('return-variables', [variableId])}
          />
        </MaximizablePanel>
      )}
    </>
  );
};

export default ParamsCalculateV1;
